import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, Bot } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t, lang, setLang, supported } = useLanguage();

  const currentLang = supported.find((l) => l.code === lang) || supported[0];

  const navLinks = [
    { label: t.nav.home, href: '#hero' },
    { label: t.nav.discover, href: '#entdecken' },
    { label: t.nav.features, href: '#funktionen' },
    { label: t.nav.benefits, href: '#vorteile' },
    { label: t.nav.faq, href: '#faq' },
    { label: t.nav.contact, href: '#kontakt' },
  ];

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="bg-dark/90 backdrop-blur-md sticky top-0 z-50 border-b border-border-dark/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3.5">
          <a href="#hero" className="flex items-center gap-2.5 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-glow ring-1 ring-white/10">
              <Bot className="w-5 h-5 text-white" aria-hidden />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-lg sm:text-xl font-bold text-white font-heading tracking-tight">
                Digi<span className="text-pink">Friendo</span>
              </span>
              <span className="text-[10px] sm:text-xs text-slate-500 font-medium tracking-wide uppercase">
                {t.brandDomain}
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-400 hover:text-primary-light transition-colors text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-2 pl-2.5 pr-3 py-2 rounded-lg bg-card-dark border border-border-dark text-slate-200 text-sm font-medium hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors"
                aria-label="Language"
                aria-expanded={langOpen}
              >
                <ReactCountryFlag
                  countryCode={currentLang.flag}
                  svg
                  style={{ width: '20px', height: '15px', borderRadius: '2px' }}
                  aria-hidden
                />
                <span className="hidden sm:inline">{currentLang.label}</span>
                <ChevronDown className="w-4 h-4 text-slate-500" aria-hidden />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-card-dark rounded-xl shadow-xl border border-border-dark py-1 z-50">
                  {supported.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/10 transition-colors text-sm ${
                        lang === l.code ? 'text-primary-light font-semibold bg-primary/5' : 'text-slate-300'
                      }`}
                    >
                      <ReactCountryFlag
                        countryCode={l.flag}
                        svg
                        style={{ width: '20px', height: '15px', borderRadius: '2px' }}
                        aria-hidden
                      />
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/s"
              className="hidden sm:inline-flex items-center bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all shadow-glow"
            >
              {t.headerCta}
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden text-slate-300 hover:text-white p-2 rounded-lg border border-border-dark bg-card-dark/50"
              aria-expanded={menuOpen}
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-border-dark py-4 pb-5">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-slate-300 hover:text-primary-light transition-colors px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-card-dark"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/s"
                className="mt-2 text-center bg-gradient-to-r from-primary to-primary-dark text-white px-6 py-3 rounded-lg font-bold text-sm"
                onClick={() => setMenuOpen(false)}
              >
                {t.headerCta}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
